/**
 * 主应用组件 - 集成认证系统和路由
 */

import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AuthPage } from './components/AuthPage';
import { Dashboard } from './components/Dashboard';
import { ResetPasswordPage } from './components/pages/ResetPasswordPage';
import { EmailVerificationPage } from './components/pages/EmailVerificationPage';
import AdminDashboard from './components/admin/AdminDashboard';

// 加载状态组件
function LoadingPage(): JSX.Element {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <div className="inline-flex items-center">
          <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span className="text-lg text-gray-600">正在验证登录状态...</span>
        </div>
      </div>
    </div>
  );
}

// 保护路由组件 - 需要登录才能访问
function ProtectedRoute({ children }: { children: React.ReactNode }): JSX.Element {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingPage />;
  }

  return isAuthenticated ? <>{children}</> : <Navigate to="/" replace />;
}

// 管理员保护路由组件 - 需要管理员权限
function AdminRoute({ children }: { children: React.ReactNode }): JSX.Element {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return <LoadingPage />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to="/prompts" replace />;
  }

  return <>{children}</>;
}

// 公开路由组件 - 已登录用户重定向到仪表板
function PublicRoute({ children }: { children: React.ReactNode }): JSX.Element {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingPage />;
  }

  return !isAuthenticated ? <>{children}</> : <Navigate to="/prompts" replace />;
}

// 应用主路由组件
function AppRouter(): JSX.Element {
  return (
    <Routes>
      {/* 公开路由 */}
      <Route path="/" element={
        <PublicRoute>
          <AuthPage />
        </PublicRoute>
      } />
      
      {/* 密码重置路由 - 不需要登录 */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* 邮箱验证路由 - 不需要登录 */}
      <Route path="/verify-email" element={<EmailVerificationPage />} />
      
      {/* 保护路由 */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      
      {/* 提示词库作为默认首页 */}
      <Route path="/prompts" element={
        <ProtectedRoute>
          <Dashboard defaultPage="prompts" />
        </ProtectedRoute>
      } />
      
      {/* 管理员后台路由 */}
      <Route path="/admin" element={
        <AdminRoute>
          <AdminDashboard />
        </AdminRoute>
      } />
      
      {/* 重定向到默认页面 */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// 主应用组件
function App(): JSX.Element {
  return (
    <Router>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </Router>
  );
}

export default App; 